import React, { useEffect, useState, useCallback } from 'react';
import {
  Table, Tag, Select, Space, Typography, Card,
  Avatar, Tooltip, Input,
} from 'antd';
import {
  UserOutlined, SearchOutlined, LoginOutlined, MailOutlined,
  TeamOutlined, DeleteOutlined, SettingOutlined, ImportOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import { theme as antdTheme } from 'antd';
import axios from 'axios';
import AdminDateFilter, { resolveRange } from '../components/AdminDateFilter';

const { Title, Text } = Typography;
const { Option } = Select;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const ACTION_LABEL = {
  login:                'Signed in',
  password_changed:     'Password changed',
  profile_updated:      'Profile updated',
  campaign_created:     'Campaign created',
  campaign_updated:     'Campaign edited',
  campaign_duplicated:  'Campaign duplicated',
  campaign_deleted:     'Campaign deleted',
  campaign_sent:        'Campaign sent',
  campaign_scheduled:   'Campaign scheduled',
  campaign_test_sent:   'Test email sent',
  list_created:         'List created',
  list_updated:         'List updated',
  list_deleted:         'List deleted',
  subscriber_added:     'Subscriber added',
  subscriber_updated:   'Subscriber updated',
  subscriber_deleted:   'Subscriber deleted',
  subscribers_imported: 'Subscribers imported',
  subscribers_exported: 'Subscribers exported',
  template_created:     'Template created',
  template_updated:     'Template updated',
  template_deleted:     'Template deleted',
  ses_settings_saved:   'SES credentials saved',
  ses_tested:           'SES connection tested',
};

const ACTION_COLOR = {
  login:                'blue',
  password_changed:     'orange',
  profile_updated:      'cyan',
  campaign_created:     'geekblue',
  campaign_updated:     'geekblue',
  campaign_duplicated:  'geekblue',
  campaign_deleted:     'red',
  campaign_sent:        'green',
  campaign_scheduled:   'lime',
  campaign_test_sent:   'purple',
  list_created:         'cyan',
  list_updated:         'cyan',
  list_deleted:         'red',
  subscriber_added:     'teal',
  subscriber_deleted:   'red',
  subscribers_imported: 'green',
  subscribers_exported: 'gold',
  template_created:     'purple',
  template_updated:     'purple',
  template_deleted:     'red',
  ses_settings_saved:   'orange',
  ses_tested:           'orange',
};

const ACTION_ICON = {
  login:                <LoginOutlined />,
  password_changed:     <SettingOutlined />,
  profile_updated:      <UserOutlined />,
  campaign_created:     <MailOutlined />,
  campaign_updated:     <MailOutlined />,
  campaign_duplicated:  <MailOutlined />,
  campaign_deleted:     <DeleteOutlined />,
  campaign_sent:        <MailOutlined />,
  campaign_scheduled:   <MailOutlined />,
  campaign_test_sent:   <MailOutlined />,
  list_created:         <TeamOutlined />,
  list_updated:         <TeamOutlined />,
  list_deleted:         <DeleteOutlined />,
  subscriber_added:     <UserOutlined />,
  subscriber_deleted:   <DeleteOutlined />,
  subscribers_imported: <ImportOutlined />,
  subscribers_exported: <ImportOutlined />,
  ses_settings_saved:   <SettingOutlined />,
  ses_tested:           <SettingOutlined />,
};

// Group similar actions for the filter dropdown
const ACTION_GROUPS = [
  { label: '🔐 Auth',       actions: ['login', 'password_changed', 'profile_updated'] },
  { label: '✉️ Campaigns',  actions: ['campaign_created', 'campaign_updated', 'campaign_duplicated', 'campaign_deleted', 'campaign_sent', 'campaign_scheduled', 'campaign_test_sent'] },
  { label: '📂 Lists',      actions: ['list_created', 'list_updated', 'list_deleted'] },
  { label: '👤 Subscribers', actions: ['subscriber_added', 'subscriber_updated', 'subscriber_deleted', 'subscribers_imported', 'subscribers_exported'] },
  { label: '🖼️ Templates',  actions: ['template_created', 'template_updated', 'template_deleted'] },
  { label: '☁️ Settings',   actions: ['ses_settings_saved', 'ses_tested'] },
];

const fmtDate = d => new Date(d).toLocaleString('en-GB', {
  day: 'numeric', month: 'short', year: 'numeric',
  hour: '2-digit', minute: '2-digit',
});

const AccountActivity = () => {
  const { token: tk } = antdTheme.useToken();

  const [data, setData]               = useState([]);
  const [total, setTotal]             = useState(0);
  const [loading, setLoading]         = useState(true);
  const [page, setPage]               = useState(1);
  const [actionF, setActionF]         = useState('');
  const [search, setSearch]           = useState('');
  const [preset, setPreset]           = useState('30 days');
  const [customRange, setCustomRange] = useState(null);

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const { from, to } = resolveRange(preset, customRange);
      const params = { page: p, limit: 50 };
      if (actionF) params.action = actionF;
      if (from)    params.from   = from;
      if (to)      params.to     = to;
      const res = await axios.get('/api/activity', { headers: authHeader(), params });
      let rows = res.data.data;
      if (search.trim()) {
        const q = search.toLowerCase();
        rows = rows.filter(r =>
          (r.user_email || '').toLowerCase().includes(q) ||
          (r.entity_name || '').toLowerCase().includes(q)
        );
      }
      setData(rows);
      setTotal(res.data.total);
      setPage(p);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [actionF, search, preset, customRange]);

  useEffect(() => { load(1); }, [load]);

  const parseMeta = (meta) => {
    if (!meta) return {};
    try { return JSON.parse(meta); } catch { return {}; }
  };

  const columns = [
    {
      title: 'User', key: 'user', width: 180, ellipsis: true,
      render: (_, r) => (
        <Space>
          <Avatar size={30} icon={<UserOutlined />} style={{ background: 'var(--brand-primary)', fontSize: 12, flexShrink: 0 }} />
          <span title={r.user_email || `User #${r.user_id}`} style={{ fontSize: 12, wordBreak: 'break-word', overflowWrap: 'anywhere', maxWidth: 130, display: 'inline-block' }}>
            {r.user_email || `User #${r.user_id}`}
          </span>
        </Space>
      ),
    },
    {
      title: 'Activity', dataIndex: 'action', key: 'action', width: 180, ellipsis: true,
      render: a => (
        <Space size={6}>
          <span style={{ fontSize: 15, opacity: 0.75 }}>{ACTION_ICON[a] || <HistoryOutlined />}</span>
          <Tag color={ACTION_COLOR[a] || 'default'} style={{ margin: 0 }}>
            {ACTION_LABEL[a] || a.replace(/_/g, ' ')}
          </Tag>
        </Space>
      ),
    },
    {
      title: 'Target', key: 'target', responsive: ['md'],
      render: (_, r) => r.entity_name ? (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: 13 }}>{r.entity_name}</Text>
          {r.entity_type && (
            <Text type="secondary" style={{ fontSize: 11, textTransform: 'capitalize' }}>
              {r.entity_type}{r.entity_id ? ` #${r.entity_id}` : ''}
            </Text>
          )}
        </Space>
      ) : <Text type="secondary">—</Text>,
    },
    {
      title: 'Details', key: 'meta', responsive: ['sm'],
      render: (_, r) => {
        const meta = parseMeta(r.meta);
        const entries = Object.entries(meta).filter(([, v]) => v !== null && v !== undefined && !Array.isArray(v));
        if (!entries.length) return <Text type="secondary" style={{ fontSize: 12 }}>—</Text>;
        return (
          <Space size={4} wrap>
            {entries.slice(0, 3).map(([k, v]) => (
              <Tooltip key={k} title={k}>
                <Tag style={{ fontSize: 11, margin: 0 }}>{k}: <strong>{String(v)}</strong></Tag>
              </Tooltip>
            ))}
          </Space>
        );
      },
    },
    {
      title: 'When', dataIndex: 'created_at', key: 'created_at', width: 160,
      render: d => <Text type="secondary" style={{ fontSize: 12 }}>{fmtDate(d)}</Text>,
    },
  ];

  return (
    <div style={{ padding: '0 2px' }}>
      <div style={{ marginBottom: 20 }}>
        <Title level={4} style={{ marginBottom: 4 }}>Account Activity</Title>
        <Text type="secondary">A log of all actions taken by members of your organisation.</Text>
      </div>

      <AdminDateFilter
        preset={preset}
        setPreset={setPreset}
        customRange={customRange}
        setCustomRange={setCustomRange}
        onRefresh={() => load(1)}
        loading={loading}
      />

      <Card
        style={{ borderRadius: 12, border: `1px solid ${tk.colorBorderSecondary}` }}
        styles={{ body: { padding: 0 } }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          padding: '14px 20px', borderBottom: `1px solid ${tk.colorBorderSecondary}`,
        }}>
          <Input
            placeholder="Search user or resource name…"
            prefix={<SearchOutlined style={{ color: tk.colorTextQuaternary }} />}
            value={search}
            onChange={e => setSearch(e.target.value)}
            allowClear
            style={{ width: 240, borderRadius: 8 }}
          />
          <Select
            value={actionF || 'all'}
            onChange={v => setActionF(v === 'all' ? '' : v)}
            style={{ width: 200 }}
          >
            <Option value="all">All Activities</Option>
            {ACTION_GROUPS.map(g => (
              <Select.OptGroup key={g.label} label={g.label}>
                {g.actions.map(a => (
                  <Option key={a} value={a}>{ACTION_LABEL[a]}</Option>
                ))}
              </Select.OptGroup>
            ))}
          </Select>
          <Text type="secondary" style={{ marginLeft: 'auto', fontSize: 12 }}>
            {total.toLocaleString()} events
          </Text>
        </div>

        {total === 0 && !loading ? (
          <div style={{ padding: '60px 24px', textAlign: 'center' }}>
            <HistoryOutlined style={{ fontSize: 40, color: tk.colorTextQuaternary, display: 'block', marginBottom: 12 }} />
            <Text type="secondary" style={{ fontSize: 14 }}>No activity recorded in this period.</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Actions like sending campaigns, importing subscribers, and changing settings will appear here.
            </Text>
          </div>
        ) : (
          <Table
            rowKey="id"
            dataSource={data}
            columns={columns}
            loading={loading}
            size="small"
            scroll={{ x: 'max-content' }}
            pagination={{
              current: page,
              pageSize: 50,
              total,
              onChange: p => load(p),
              showTotal: n => `${n} events`,
              style: { padding: '12px 20px', margin: 0 },
            }}
            style={{ borderRadius: '0 0 12px 12px', overflow: 'hidden' }}
          />
        )}
      </Card>
    </div>
  );
};

export default AccountActivity;
